import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { displayValue, formatLabel, InfoRow, SectionCard } from './shared';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    value !== null && typeof value === 'object' && !Array.isArray(value);

const renderPrimitiveArray = (items: unknown[]) => (
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {items.map((item, index) => (
            <li key={index} style={{ fontSize: '13px', marginBottom: 4 }}>
                {displayValue(item)}
            </li>
        ))}
    </Box>
);

const renderObjectArray = (items: Record<string, unknown>[]) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {items.map((item, index) => (
            <Paper key={index} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', display: 'block', mb: 1 }}>
                    Item {index + 1}
                </Typography>
                {Object.entries(item).map(([key, val]) => (
                    <InfoRow key={key} label={formatLabel(key)} value={renderValue(val, key)} />
                ))}
            </Paper>
        ))}
    </Box>
);

const renderValue = (value: unknown, key?: string): React.ReactNode => {
    if (value === null || value === undefined || value === '') {
        return displayValue(value);
    }

    if (Array.isArray(value)) {
        if (value.length === 0) return displayValue(null);
        if (value.every((item) => !isPlainObject(item))) {
            return renderPrimitiveArray(value);
        }
        return renderObjectArray(value as Record<string, unknown>[]);
    }

    if (isPlainObject(value)) {
        return (
            <Box sx={{ pl: key ? 0 : 0 }}>
                {Object.entries(value).map(([nestedKey, nestedVal]) => (
                    <InfoRow
                        key={nestedKey}
                        label={formatLabel(nestedKey)}
                        value={renderValue(nestedVal, nestedKey)}
                    />
                ))}
            </Box>
        );
    }

    return displayValue(value);
};

interface Props {
    formData: Record<string, unknown>;
}

export default function GenericFormDataView({ formData }: Props) {
    const entries = Object.entries(formData || {});

    if (entries.length === 0) {
        return (
            <SectionCard title="Form Data">
                <Typography color="text.secondary">No form data</Typography>
            </SectionCard>
        );
    }

    return (
        <>
            {entries.map(([key, value]) => {
                if (isPlainObject(value) || Array.isArray(value)) {
                    return (
                        <SectionCard key={key} title={formatLabel(key)}>
                            {renderValue(value, key)}
                        </SectionCard>
                    );
                }

                return (
                    <SectionCard key={key} title="Form Data">
                        <InfoRow label={formatLabel(key)} value={displayValue(value)} />
                    </SectionCard>
                );
            })}
        </>
    );
}
